import React, { useEffect, useState } from 'react';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import SubtleBadge from '@/components/common/SubtleBadge';
import accessLevelApi from '@/app/common/api/accessLevelApi';
import Select, { type SingleValue } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCheckAccessQuery } from '@/app/common/api/accessLevelApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import searchVolumeApi, {
  useStartVolumeMutation
} from '@/app/KeywordResearch/api/searchVolumeApi';
import { Formik } from 'formik';
import { Button, Card, Form, Spinner, Alert } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import { errorMessage, warningMessage } from '@/utils/toast';
import { array, object, string } from 'yup';
const LIMIT_SEARCH_VOLUME_KEYWORDS = 200;

import geoLanguagesData from '@/app/common/api/backups/languages.json';
import geoLocationsData from '@/app/common/api/backups/locations.json';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { getColor, toPersianDigit } from '@/helpers/utils';
import useDomain from '@/hooks/useDomain';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';

type SelectOption = {
  value: string;
  label: string;
};

type CountryOption = SelectOption & {
  flag?: string;
};

type SearchVolumeFormValues = {
  keyword: string[];
};

const ALL_LOCATIONS_VALUE = 'all';
const ALL_LANGUAGES_VALUE = 'all';
const GLOBAL_FLAG_URL = 'https://static.mizfa.tools/flags/global.webp';

const WORLDWIDE_OPTION: CountryOption = {
  value: ALL_LOCATIONS_VALUE,
  label: 'کل جهان',
  flag: GLOBAL_FLAG_URL
};

const ALL_LANGUAGES_OPTION: SelectOption = {
  value: ALL_LANGUAGES_VALUE,
  label: 'همه زبان ها'
};

const SEARCH_VOLUME_SUBMIT_ERROR_MESSAGE =
  'در ارسال درخواست خطایی رخ داده هست لطفا مجددا تلاش کنید.';

const SEARCH_VOLUME_NO_DATA_MESSAGE =
  'داده ای برای کلمه کلیدی درخواستی یافت نشد!';

const SearchVolumeForm = () => {
  const { loc, lang } = useParams();
  const { domain_slug } = useDomain();
  const [geoLocationsCountries, setGeoLocationsCountries] =
    useState<CountryOption[]>();
  const [geoLanguages, setGeoLanguages] = useState<SelectOption[]>();
  const [startVolume, { isLoading }] = useStartVolumeMutation();
  const dispatch = useDispatch();
  const { data: userInfo } = useGetUserInfoQuery();

  const [selectedPlace, setSelectedPlace] = useState<CountryOption>({
    value: 'ir',
    label: 'ایران',
    flag: `https://static.mizfa.tools/flags/ir.webp`
  });

  const [selectedLanguage, setSelectedLanguage] = useState<SelectOption>({
    value: 'fa',
    label: 'فارسی'
  });
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  useEffect(() => {
    if (geoLocationsData) {
      const geoCountries: CountryOption[] = [WORLDWIDE_OPTION];
      geoLocationsData.data.map(({ code, fa_name }) =>
        geoCountries.push({
          value: code,
          label: fa_name,
          flag: `https://static.mizfa.tools/flags/${code}.webp`
        })
      );

      setGeoLocationsCountries(geoCountries);

      if (loc === ALL_LOCATIONS_VALUE) {
        setSelectedPlace(WORLDWIDE_OPTION);
      } else if (loc && loc !== 'ir') {
        const geo = geoLocationsData.data.find(item => item.code === loc);

        if (geo) {
          setSelectedPlace({
            value: geo.code,
            label: geo.fa_name,
            flag: `https://static.mizfa.tools/flags/${geo.code}.webp`
          });
        }
      }
    }
  }, [loc]);

  useEffect(() => {
    if (geoLanguagesData) {
      const languages: SelectOption[] = [ALL_LANGUAGES_OPTION];
      geoLanguagesData.data.map(({ code, fa_name }) =>
        languages.push({
          value: code,
          label: fa_name
        })
      );

      setGeoLanguages(languages);

      if (lang === ALL_LANGUAGES_VALUE) {
        setSelectedLanguage(ALL_LANGUAGES_OPTION);
      } else if (lang && lang !== 'fa') {
        const geo = geoLanguagesData.data.find(item => item.code === lang);

        if (geo) {
          setSelectedLanguage({
            value: geo.code,
            label: geo.fa_name
          });
        }
      }
    }
  }, [lang]);

  const { data: accessData } = useCheckAccessQuery({
    domain_slug,
    tool_name: 'search_volume'
  });

  const onSubmit = async (
    values: SearchVolumeFormValues
  ) => {
    await startVolume({
      requested_user: {
        name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
        email: userInfo?.data?.email ?? ''
      },
      keywords: values.keyword.filter(word => word !== ''),
      location_code:
        selectedPlace.value === ALL_LOCATIONS_VALUE
          ? null
          : selectedPlace.value,
      language_code:
        selectedLanguage.value === ALL_LANGUAGES_VALUE
          ? null
          : selectedLanguage.value,
      domain_slug
    })
      .unwrap()
      .then(response => {
        if (response.code !== 200) {
          if (response.code === 422) {
            const messages = response?.msg?.split('|');
            messages?.slice(0, 6).forEach(msg => warningMessage(msg));
            warningMessage(String(response.data));
          } else {
            errorMessage(SEARCH_VOLUME_SUBMIT_ERROR_MESSAGE);
          }
        } else if (response?.data) {
          dispatch(
            searchVolumeApi.util.invalidateTags(['SearchVolumeHistory'])
          );
          dispatch(accessLevelApi.util.invalidateTags(['AccessLevel']));
        } else {
          warningMessage(SEARCH_VOLUME_NO_DATA_MESSAGE);
        }
      })
      .catch(err => {
        errorMessage(SEARCH_VOLUME_SUBMIT_ERROR_MESSAGE);
        console.log(err);
      });
  };

  return (
    <Card
      style={{
        minHeight: 347
      }}
    >
      <FalconCardHeader
        title={
          <div className="mb-1">
            <span className="d-md-block">
              هر کلمه رو در یک
              <span className="fw-bold"> خط جدید </span>
              بنویس
              <HelpTooltip html={keywordResearchTooltips.massive.helper} />
            </span>
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
      />
      <Card.Body className='py-1'>
        <Formik<SearchVolumeFormValues>
          onSubmit={onSubmit}
          initialValues={{
            keyword: []
          }}
          validationSchema={object().shape({
            keyword: array()
              .of(string())
              .test('not-empty', 'کلمات رو وارد کنید.', value => {
                if (!value || value.length === 0) return false;
                if (
                  value.length === 1 &&
                  (value[0] === undefined ||
                    value[0] === null ||
                    value[0].trim() === '')
                ) {
                  return false;
                }
                return true;
              })
              .max(
                LIMIT_SEARCH_VOLUME_KEYWORDS,
                'تعداد کلمات بیش از حد مجاز است.'
              )
          })}
        >
          {({
            values,
            errors,
            handleBlur,
            handleSubmit,
            setFieldValue,
            touched
          }) => (
            <div className={`mt-3 mb-2`}>
              <Form onSubmit={handleSubmit}>
                <Form.Control
                  as="textarea"
                  rows={5}
                  isInvalid={!!(touched.keyword && errors.keyword)}
                  isValid={!!(touched.keyword && !errors.keyword)}
                  name="keyword"
                  onBlur={handleBlur}
                  onChange={e => {
                    setFieldValue(
                      'keyword',
                      (e.target as HTMLTextAreaElement).value.split('\n')
                    );
                  }}
                  value={values.keyword.join('\n')}
                  placeholder={`کلمه کلیدی ۱\nکلمه کلیدی ۲\nکلمه کلیدی ۳\n...`}
                />
                <div className="my-2 fs--1 mb-4 d-flex flex-wrap gap-1 align-items-center">
                  {values.keyword.filter(word => word !== '').length <=
                  LIMIT_SEARCH_VOLUME_KEYWORDS ? (
                    <>
                      <SubtleBadge
                        bg={
                          values.keyword.filter(word => word !== '').length <=
                          Math.floor(LIMIT_SEARCH_VOLUME_KEYWORDS / 2)
                            ? 'success'
                            : 'warning'
                        }
                        className="fs--2"
                      >
                        {LIMIT_SEARCH_VOLUME_KEYWORDS -
                          values.keyword.filter(word => word !== '').length}
                      </SubtleBadge>
                      &nbsp; کلمه دیگه میتونی وارد کنی
                    </>
                  ) : (
                    <>
                      <SubtleBadge bg={'danger'} className="fs--2">
                        {(LIMIT_SEARCH_VOLUME_KEYWORDS -
                          values.keyword.filter(word => word !== '').length) *
                          -1}
                      </SubtleBadge>
                      &nbsp; کلمه کلیدی اضافه وارد کردی
                      <FontAwesomeIcon
                        icon="exclamation-circle"
                        className="text-danger"
                      />
                    </>
                  )}
                  <div className="mt-2">
                    {errors.keyword ? (
                      <Form.Label className="text-danger">
                        {errors.keyword}
                      </Form.Label>
                    ) : (
                      <Form.Label className="text-danger"> </Form.Label>
                    )}
                  </div>
                </div>
                <div className="d-flex w-100 position-relative">
                  <div style={{ flex: 1, minWidth: 0, zIndex: 5 }}>
                    <Select<CountryOption>
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={true}
                      onChange={(e: SingleValue<CountryOption>) => {
                        if (!e) return;
                        setSelectedPlace(e);

                        if (e.value === ALL_LOCATIONS_VALUE) {
                          return;
                        }

                        const location = geoLocationsData.data.find(
                          item => item.code === e.value
                        );
                        const { official_locaguage_code } = location ?? {
                          official_locaguage_code: null
                        };

                        let defaultLanguage = {
                          code: 'en',
                          fa_name: 'انگلیسی'
                        };
                        if (official_locaguage_code) {
                          const language = geoLanguagesData.data.find(
                            item => item.code === official_locaguage_code
                          );
                          if (language) defaultLanguage = language;
                        }

                        setSelectedLanguage({
                          value: defaultLanguage.code,
                          label: defaultLanguage.fa_name
                        });
                      }}
                      options={geoLocationsCountries}
                      classNamePrefix="react-select"
                      value={selectedPlace}
                      maxMenuHeight={300}
                      styles={{
                        container: baseStyles => ({
                          ...baseStyles,
                          width: '100%'
                        }),
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          width: '100%',
                          cursor: 'pointer',
                          borderRadius: '0px 5px 5px 0px'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        }),
                        menu: base => ({
                          ...base,
                          width: '100%',
                          minWidth: '200px'
                        })
                      }}
                      components={{
                        Option: OptionLayoutPlace,
                        SingleValue: SingleValueFlag
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, zIndex: 4 }}>
                    <Select<SelectOption>
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={true}
                      onChange={(e: SingleValue<SelectOption>) => {
                        if (e) setSelectedLanguage(e);
                      }}
                      options={geoLanguages}
                      classNamePrefix="react-select"
                      value={selectedLanguage}
                      maxMenuHeight={300}
                      styles={{
                        container: baseStyles => ({
                          ...baseStyles,
                          width: '100%'
                        }),
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          cursor: 'pointer',
                          width: '100%',
                          borderRadius: '0px'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        }),
                        menu: base => ({
                          ...base,
                          width: '100%',
                          minWidth: '150px'
                        })
                      }}
                      components={{
                        Option: OptionLayoutPlace,
                        SingleValue: SingleValueFlag
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <ChildrensTooltip
                      placement="top"
                      className="p-0 d-block w-100 h-100"
                      html={isAccessAllowed() ? '' : 'شما دسترسی لازم را ندارید'}
                    >
                      <Button
                        variant="falcon-primary"
                        className="d-flex px-3 align-items-center justify-content-center h-100 w-100 rounded-start-0"
                        type="submit"
                        disabled={
                          !isAccessAllowed() ||
                          isLoading ||
                          (accessData?.data?.budget_used ?? 0) >=
                            (accessData?.data?.total_budget ?? 0)
                        }
                      >
                      <span className="w-100 fs--1">
                        {isLoading ? (
                          <>
                            <Spinner
                              style={{
                                width: '16px',
                                height: '16px'
                              }}
                              className="me-1"
                            />
                            <span className="d-lg-inline fs-0 d-none">
                              درحال اجرا
                            </span>
                          </>
                        ) : (
                          <>
                            <span className=" d-lg-inline d-none">
                              <span className="fs-0 me-1">اجرا</span>
                              <span className="opacity-75 fs--1">
                                (
                                {toPersianDigit(
                                  String(accessData?.data?.budget_used ?? '')
                                )}{' '}
                                از
                                {toPersianDigit(
                                  String(accessData?.data?.total_budget ?? '')
                                )}
                                <HelpTooltip
                                  color={getColor('black')}
                                  placement="top"
                                  html="این محدودیت ماهانه است."
                                />
                                )
                              </span>
                            </span>
                            <span className="d-lg-none d-inline">
                              <span className="opacity-75 fs--1">
                                {toPersianDigit(
                                  String(accessData?.data?.budget_used ?? '')
                                )}{' '}
                                /
                                {toPersianDigit(
                                  String(accessData?.data?.total_budget ?? '')
                                )}
                                <HelpTooltip
                                  className="ms-1"
                                  color={getColor('black')}
                                  placement="top"
                                  html="این محدودیت ماهانه است."
                                />
                              </span>
                            </span>
                          </>
                        )}
                      </span>
                    </Button>
                    </ChildrensTooltip>
                  </div>
                </div>
              </Form>
            </div>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default SearchVolumeForm;
